export type ProviderAccountType =
  | "studio_owner"
  | "studio_staff"
  | "wenlix_internal_manager";

export type ProviderUiRole =
  | "owner"
  | "client"
  | "staff"
  | "manager"
  | "supervisor"
  | "director";

export interface ProviderAuthConfig {
  issuer: string;
  clientId: string;
  audience: string;
  clientOrganizationId: string;
  internalOrganizationId: string;
  redirectUri: string;
  sessionEndpoint: string;
}

export type ProviderAuthorizationPrompt = "login" | "none";

export interface ProviderAuthorizationAttempt {
  state: string;
  nonce: string;
  verifier: string;
  challenge: string;
  redirectUri: string;
  expectedAccountUuid: string | null;
  prompt: ProviderAuthorizationPrompt;
  createdAt: number;
}

export interface ProviderAuthorizationRequest extends ProviderAuthorizationAttempt {
  authorizationUrl: string;
}

export interface ProviderTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface ProviderProfile {
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
}

export interface ProviderCoreSessionProjection {
  supportOnly?: false;
  accountUuid: string;
  accountType: ProviderAccountType;
  subject: string;
  organizationId: string;
  clientId: string;
  consumerSurface: "internal_app";
  studioId: string | null;
  legacySource: "app";
  legacyId: string;
  legacyProjection: true;
  businessRole: string;
  permissions: string[];
  businessApi: "core" | "staff";
  profile: ProviderProfile;
  uiRole: ProviderUiRole;
}

// Support outage bootstrap proves a named internal identity, never a legacy
// Core projection. Ordinary business routes must reject this surface variant.
export interface ProviderSupportSessionProjection {
  supportOnly: true;
  accountUuid: string;
  accountType: "wenlix_internal_manager";
  subject: string;
  organizationId: string;
  clientId: string;
  consumerSurface: "internal_app";
  studioId: null;
  businessRole: "support_operator";
  permissions: string[];
  businessApi: "support";
  profile: ProviderProfile;
  uiRole: "manager";
}
export type ProviderSessionProjection = ProviderCoreSessionProjection | ProviderSupportSessionProjection;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface StoredProviderSlot {
  projection: ProviderSessionProjection;
  tokens: ProviderTokens;
  updatedAt: number;
}

interface StoredProviderRegistry {
  version: 1;
  activeAccountUuid: string | null;
  slots: Record<string, StoredProviderSlot>;
}

export interface ProviderSlotSummary {
  accountUuid: string;
  displayName: string;
  email: string;
  expiresAt: number;
}

export interface ProviderLoginOption {
  label: string;
  loginHint: string;
}

export class ProviderAuthorizationError extends Error {
  readonly code: string;
  readonly description: string | null;
  readonly prompt: ProviderAuthorizationPrompt;

  constructor(
    code: string,
    description: string | null,
    prompt: ProviderAuthorizationPrompt = "login",
  ) {
    super(`provider denied authorization (${code})`);
    this.name = "ProviderAuthorizationError";
    this.code = code;
    this.description = description;
    this.prompt = prompt;
  }
}

export type AuthSource = "legacy" | "provider" | null;

const providerRegistryKey = "wenlix-provider-auth-v1";
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export function isCurrentProviderResponse(
  boundAccountUuid: string | null,
  activeAccountUuid: string | null,
): boolean {
  return boundAccountUuid === activeAccountUuid;
}

export function isCurrentAuthResponse(
  boundAuthSource: AuthSource,
  boundAccountUuid: string | null,
  activeAuthSource: AuthSource,
  activeAccountUuid: string | null,
): boolean {
  if (boundAuthSource === "provider") {
    return activeAuthSource === "provider" &&
      boundAccountUuid !== null &&
      boundAccountUuid === activeAccountUuid;
  }
  if (boundAuthSource === "legacy") {
    return boundAccountUuid === null && activeAuthSource === "legacy";
  }
  return boundAccountUuid === null && activeAuthSource === null;
}

export function isDispatchableAuthRequest(
  boundAuthSource: AuthSource | undefined,
  boundAccountUuid: string | null,
  activeAuthSource: AuthSource,
  activeAccountUuid: string | null,
): boolean {
  if (boundAuthSource === undefined) return boundAccountUuid === null;
  return isCurrentAuthResponse(
    boundAuthSource,
    boundAccountUuid,
    activeAuthSource,
    activeAccountUuid,
  );
}

function redactAuthorizationHeaders(headers: unknown): void {
  if (!headers || typeof headers !== "object") return;

  const mutableHeaders = headers as Record<string, unknown> & {
    delete?: (name: string) => unknown;
  };
  if (typeof mutableHeaders.delete === "function") {
    try {
      mutableHeaders.delete("Authorization");
      mutableHeaders.delete("authorization");
    } catch {
      // Continue with enumerable header deletion for plain-object adapters.
    }
  }
  for (const key of Object.keys(mutableHeaders)) {
    if (key.toLowerCase() !== "authorization") continue;
    try {
      delete mutableHeaders[key];
    } catch {
      // AxiosHeaders is mutable; this only protects unusual frozen adapters.
    }
  }
}

export function redactAuthorizationFromError<T>(error: T): T {
  if (!error || typeof error !== "object") return error;

  const candidate = error as Record<string, unknown>;
  const config = candidate.config as Record<string, unknown> | undefined;
  const response = candidate.response as Record<string, unknown> | undefined;
  const responseConfig = response?.config as Record<string, unknown> | undefined;
  const request = candidate.request as Record<string, unknown> | undefined;

  redactAuthorizationHeaders(config?.headers);
  redactAuthorizationHeaders(responseConfig?.headers);
  redactAuthorizationHeaders(request?.headers);
  redactAuthorizationHeaders(request?._headers);
  return error;
}

export function isCurrentProviderFailure(
  expectedAccountUuid: string | null,
  activeAccountUuid: string | null,
  projectedAccountUuid: string | null,
): boolean {
  return expectedAccountUuid === null ||
    expectedAccountUuid === activeAccountUuid ||
    (activeAccountUuid === null && projectedAccountUuid === expectedAccountUuid);
}
const numericIdPattern = /^[1-9][0-9]*$/;
const providerLoginHintPattern = /^w_m_[0-9a-f]{32}$/;
const attemptLifetimeMs = 15 * 60 * 1_000;

function required(value: string | undefined, name: string): string {
  const normalized = value?.trim() ?? "";
  if (!normalized) {
    throw new Error(`provider ${name} is required`);
  }
  return normalized;
}

function exactNumericId(value: string | undefined, name: string): string {
  const normalized = required(value, name);
  if (!numericIdPattern.test(normalized)) {
    throw new Error(`provider ${name} must be an exact numeric ID`);
  }
  return normalized;
}

function parseSecureUrl(value: string, name: string): URL {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error(`provider ${name} must be an absolute URL`);
  }
  const loopback =
    parsed.protocol === "http:" &&
    (parsed.hostname === "127.0.0.1" || parsed.hostname === "[::1]");
  if (parsed.protocol !== "https:" && !loopback) {
    throw new Error(`provider ${name} must use HTTPS or IP loopback HTTP`);
  }
  if (parsed.username || parsed.password) {
    throw new Error(`provider ${name} must not contain credentials`);
  }
  return parsed;
}

export function parseProviderAuthConfig(
  env: Record<string, string | undefined>,
): ProviderAuthConfig | null {
  if ((env.VITE_IDENTITY_PROVIDER_MODE ?? "legacy") !== "provider") {
    return null;
  }

  const issuerUrl = parseSecureUrl(
    required(env.VITE_IDENTITY_PROVIDER_ISSUER, "issuer"),
    "issuer",
  );
  if (issuerUrl.search || issuerUrl.hash) {
    throw new Error("provider issuer must not contain query or fragment");
  }
  issuerUrl.pathname = issuerUrl.pathname.replace(/\/+$/, "") || "/";

  const redirectUrl = parseSecureUrl(
    required(env.VITE_IDENTITY_PROVIDER_REDIRECT_URI, "redirect URI"),
    "redirect URI",
  );
  if (redirectUrl.search || redirectUrl.hash) {
    throw new Error("provider redirect URI must not contain query or fragment");
  }

  const clientOrganizationId = exactNumericId(
    env.VITE_IDENTITY_PROVIDER_CLIENT_ORGANIZATION_ID,
    "client organization ID",
  );
  const internalOrganizationId = exactNumericId(
    env.VITE_IDENTITY_PROVIDER_INTERNAL_ORGANIZATION_ID,
    "internal organization ID",
  );
  if (clientOrganizationId === internalOrganizationId) {
    throw new Error("provider client and internal organizations must differ");
  }

  return {
    issuer: issuerUrl.toString().replace(/\/$/, ""),
    clientId: exactNumericId(env.VITE_IDENTITY_PROVIDER_CLIENT_ID, "client ID"),
    audience: exactNumericId(env.VITE_IDENTITY_PROVIDER_AUDIENCE, "audience"),
    clientOrganizationId,
    internalOrganizationId,
    redirectUri: redirectUrl.toString(),
    sessionEndpoint: "/api/v1/session/me",
  };
}

function randomBase64Url(byteLength: number, cryptoApi: Crypto): string {
  const bytes = new Uint8Array(byteLength);
  cryptoApi.getRandomValues(bytes);
  let raw = "";
  for (const byte of bytes) raw += String.fromCharCode(byte);
  return btoa(raw).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

async function sha256Base64Url(value: string, cryptoApi: Crypto): Promise<string> {
  const digest = await cryptoApi.subtle.digest("SHA-256", new TextEncoder().encode(value));
  let raw = "";
  for (const byte of new Uint8Array(digest)) raw += String.fromCharCode(byte);
  return btoa(raw).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

export async function validateProviderPkceAttempt(
  attempt: ProviderAuthorizationAttempt,
  cryptoApi: Crypto = globalThis.crypto,
): Promise<void> {
  const actualChallenge = await sha256Base64Url(attempt.verifier, cryptoApi);
  if (!constantTimeEqual(actualChallenge, attempt.challenge)) {
    throw new Error("provider callback PKCE verifier mismatch");
  }
}

export async function buildAuthorizationRequest(
  config: ProviderAuthConfig,
  cryptoApi: Crypto = globalThis.crypto,
  now: number = Date.now(),
  loginHint?: string,
  prompt: ProviderAuthorizationPrompt = "login",
): Promise<ProviderAuthorizationRequest> {
  const verifier = randomBase64Url(96, cryptoApi);
  const state = randomBase64Url(32, cryptoApi);
  const nonce = randomBase64Url(32, cryptoApi);
  const challenge = await sha256Base64Url(verifier, cryptoApi);
  const endpoint = new URL("/oauth/v2/authorize", `${config.issuer}/`);
  const parameters = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: "code",
    scope: [
      "openid",
      "profile",
      "email",
      "offline_access",
      "urn:zitadel:iam:user:resourceowner",
      // Login V2 derives its user-search boundary from this scope. Without an
      // explicit organization it searches the instance default organization,
      // cannot resolve Wenlix's opaque internal login, and reaches the password
      // page without a user session.
      `urn:zitadel:iam:org:id:${config.internalOrganizationId}`,
      `urn:zitadel:iam:org:project:id:${config.audience}:aud`,
    ].join(" "),
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: "S256",
    prompt,
  });
  let expectedAccountUuid: string | null = null;
  if (loginHint !== undefined) {
    if (!providerLoginHintPattern.test(loginHint)) {
      throw new Error("provider login hint must be an exact opaque account login");
    }
    const compact = loginHint.slice(4);
    const candidate = `${compact.slice(0, 8)}-${compact.slice(8, 12)}-${compact.slice(12, 16)}-${compact.slice(16, 20)}-${compact.slice(20)}`;
    if (!uuidPattern.test(candidate)) {
      throw new Error("provider login hint must contain a canonical account UUID");
    }
    expectedAccountUuid = candidate;
    parameters.set("login_hint", loginHint);
  }
  endpoint.search = parameters.toString();
  return {
    authorizationUrl: endpoint.toString(),
    state,
    nonce,
    verifier,
    challenge,
    redirectUri: config.redirectUri,
    expectedAccountUuid,
    prompt,
    createdAt: now,
  };
}

export function parseProviderLoginOptions(value: unknown): ProviderLoginOption[] {
  if (!value || typeof value !== "object") {
    throw new Error("provider login options must be an object");
  }
  const body = value as Record<string, unknown>;
  if (Object.keys(body).length !== 1 || !Array.isArray(body.options) || body.options.length > 12) {
    throw new Error("provider login options are invalid");
  }
  const seen = new Set<string>();
  return body.options.map((raw): ProviderLoginOption => {
    if (!raw || typeof raw !== "object") {
      throw new Error("provider login option is invalid");
    }
    const option = raw as Record<string, unknown>;
    if (Object.keys(option).length !== 2) {
      throw new Error("provider login option has unexpected fields");
    }
    const label = typeof option.label === "string" ? option.label.trim() : "";
    const loginHint = typeof option.login_hint === "string" ? option.login_hint : "";
    if (!label || label.length > 80) {
      throw new Error("provider login option label is invalid");
    }
    if (!providerLoginHintPattern.test(loginHint)) {
      throw new Error("provider login option login hint is invalid");
    }
    if (seen.has(loginHint)) {
      throw new Error("provider login option is duplicated");
    }
    seen.add(loginHint);
    return { label, loginHint };
  });
}

function constantTimeEqual(left: string, right: string): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= left.charCodeAt(index) ^ right.charCodeAt(index);
  }
  return difference === 0;
}

export function consumeAuthorizationCallback(
  callbackUrl: string,
  attempts: Map<string, ProviderAuthorizationAttempt>,
  now: number = Date.now(),
): { code: string; attempt: ProviderAuthorizationAttempt } {
  const callback = new URL(callbackUrl);
  if (["access_token", "refresh_token", "id_token", "token"].some((name) =>
    callback.searchParams.has(name))) {
    throw new Error("provider callback must not carry token parameters");
  }
  const states = callback.searchParams.getAll("state");
  if (states.length !== 1 || !states[0]) {
    throw new Error("provider callback must carry exactly one state");
  }
  const state = states[0];
  const attempt = attempts.get(state);
  if (!attempt || !constantTimeEqual(state, attempt.state)) {
    throw new Error("provider callback has an unknown or replayed state");
  }
  attempts.delete(state);

  const expected = new URL(attempt.redirectUri);
  if (
    callback.origin !== expected.origin ||
    callback.pathname !== expected.pathname ||
    callback.hash !== ""
  ) {
    throw new Error("provider callback redirect URI mismatch");
  }
  if (attempt.createdAt > now || now - attempt.createdAt > attemptLifetimeMs) {
    throw new Error("provider callback attempt expired");
  }

  const errors = callback.searchParams.getAll("error");
  if (errors.length > 0) {
    throw new ProviderAuthorizationError(
      errors[0],
      callback.searchParams.get("error_description"),
      attempt.prompt ?? "login",
    );
  }
  const codes = callback.searchParams.getAll("code");
  if (codes.length !== 1 || !codes[0]) {
    throw new Error("provider callback must carry exactly one code");
  }
  return { code: codes[0], attempt };
}

function exactString(value: unknown, name: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`provider ${name} is required`);
  }
  return value.trim();
}

function exactInteger(value: unknown, name: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw new Error(`provider ${name} must be an integer`);
  }
  return value;
}

function decodeBase64Url(value: string, name: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) {
    throw new Error(`provider ${name} is not base64url`);
  }
  const padded = value.replaceAll("-", "+").replaceAll("_", "/")
    .padEnd(Math.ceil(value.length / 4) * 4, "=");
  let decoded: string;
  try {
    decoded = atob(padded);
  } catch {
    throw new Error(`provider ${name} is not base64url`);
  }
  const bytes = new Uint8Array(decoded.length);
  for (let index = 0; index < decoded.length; index += 1) {
    bytes[index] = decoded.charCodeAt(index);
  }
  return bytes;
}

function decodeJwtObject(value: string, name: string): Record<string, unknown> {
  let decoded: unknown;
  try {
    decoded = JSON.parse(new TextDecoder().decode(decodeBase64Url(value, name)));
  } catch {
    throw new Error(`provider ${name} must be a JSON object`);
  }
  if (!decoded || typeof decoded !== "object" || Array.isArray(decoded)) {
    throw new Error(`provider ${name} must be a JSON object`);
  }
  return decoded as Record<string, unknown>;
}

function exactAudience(value: unknown, clientId: string, authorizedParty: unknown): void {
  if (typeof value === "string") {
    if (value !== clientId) throw new Error("provider ID token audience mismatch");
    if (authorizedParty !== undefined && authorizedParty !== clientId) {
      throw new Error("provider ID token authorized party mismatch");
    }
    return;
  }
  if (!Array.isArray(value) || value.length === 0 ||
      value.some((audience) => typeof audience !== "string") ||
      !value.includes(clientId)) {
    throw new Error("provider ID token audience mismatch");
  }
  if ((value.length > 1 || authorizedParty !== undefined) && authorizedParty !== clientId) {
    throw new Error("provider ID token authorized party mismatch");
  }
}

interface ProviderJwk extends JsonWebKey {
  alg?: string;
  kid?: string;
  use?: string;
}

function selectProviderJwk(raw: unknown, kid: string): ProviderJwk {
  if (!raw || typeof raw !== "object" || !Array.isArray((raw as { keys?: unknown }).keys)) {
    throw new Error("provider JWKS must contain keys");
  }
  const matches = (raw as { keys: unknown[] }).keys.filter((candidate): candidate is ProviderJwk => {
    if (!candidate || typeof candidate !== "object") return false;
    const key = candidate as ProviderJwk;
    return key.kid === kid && key.kty === "RSA" && key.alg === "RS256" &&
      (key.use === undefined || key.use === "sig") &&
      typeof key.n === "string" && typeof key.e === "string";
  });
  if (matches.length !== 1) {
    throw new Error("provider ID token signing key mismatch");
  }
  return matches[0];
}

export async function validateProviderIdToken(
  rawTokenResponse: unknown,
  accessToken: string,
  expectedNonce: string,
  config: ProviderAuthConfig,
  rawJwks: unknown,
  cryptoApi: Crypto = globalThis.crypto,
  now: number = Date.now(),
  expectedOrganizationId?: string,
): Promise<string> {
  if (!rawTokenResponse || typeof rawTokenResponse !== "object") {
    throw new Error("provider token response must be an object");
  }
  const idToken = exactString(
    (rawTokenResponse as Record<string, unknown>).id_token,
    "ID token",
  );
  const segments = idToken.split(".");
  if (segments.length !== 3 || segments.some((segment) => segment === "")) {
    throw new Error("provider ID token must be a compact JWT");
  }
  const [encodedHeader, encodedPayload, encodedSignature] = segments;
  const header = decodeJwtObject(encodedHeader, "ID token header");
  if (header.alg !== "RS256" || typeof header.kid !== "string" || !header.kid ||
      Object.hasOwn(header, "jwk") || Object.hasOwn(header, "jku") || Object.hasOwn(header, "x5u") ||
      Object.hasOwn(header, "crit")) {
    throw new Error("provider ID token header is not allowed");
  }
  const signingKey = selectProviderJwk(rawJwks, header.kid);
  const cryptoKey = await cryptoApi.subtle.importKey(
    "jwk",
    signingKey,
    { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
    false,
    ["verify"],
  );
  const signatureValid = await cryptoApi.subtle.verify(
    "RSASSA-PKCS1-v1_5",
    cryptoKey,
    decodeBase64Url(encodedSignature, "ID token signature"),
    new TextEncoder().encode(`${encodedHeader}.${encodedPayload}`),
  );
  if (!signatureValid) throw new Error("provider ID token signature mismatch");

  const claims = decodeJwtObject(encodedPayload, "ID token payload");
  if (expectedOrganizationId !== undefined && claims["urn:zitadel:iam:user:resourceowner:id"] !== expectedOrganizationId) {
    throw new Error("provider ID token internal organization mismatch");
  }
  if (claims.iss !== config.issuer) throw new Error("provider ID token issuer mismatch");
  exactAudience(claims.aud, config.clientId, claims.azp);
  const nowSeconds = Math.floor(now / 1_000);
  const expiresAt = exactInteger(claims.exp, "ID token expiry");
  const issuedAt = exactInteger(claims.iat, "ID token issued-at");
  if (expiresAt <= nowSeconds) throw new Error("provider ID token expired");
  if (expiresAt <= issuedAt) throw new Error("provider ID token expiry must follow issued-at");
  if (issuedAt > nowSeconds + 60) throw new Error("provider ID token issued-at is in the future");
  if (claims.nbf !== undefined) {
    const notBefore = exactInteger(claims.nbf, "ID token not-before");
    if (expiresAt <= notBefore) {
      throw new Error("provider ID token expiry must follow not-before");
    }
    if (notBefore > nowSeconds + 60) throw new Error("provider ID token is not active");
  }
  const nonce = exactString(claims.nonce, "ID token nonce");
  if (!constantTimeEqual(nonce, expectedNonce)) {
    throw new Error("provider ID token nonce mismatch");
  }
  if (claims.at_hash !== undefined) {
    const digest = new Uint8Array(await cryptoApi.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(accessToken),
    ));
    let raw = "";
    for (const byte of digest.slice(0, digest.length / 2)) raw += String.fromCharCode(byte);
    const expectedHash = btoa(raw).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
    if (!constantTimeEqual(exactString(claims.at_hash, "ID token access hash"), expectedHash)) {
      throw new Error("provider ID token access hash mismatch");
    }
  }
  return canonicalUuid(claims.sub, "ID token subject");
}

export function parseProviderTokens(raw: unknown, now: number = Date.now()): ProviderTokens {
  if (!raw || typeof raw !== "object") {
    throw new Error("provider token response must be an object");
  }
  const body = raw as Record<string, unknown>;
  const accessToken = exactString(body.access_token, "access token");
  const refreshToken = exactString(body.refresh_token, "refresh token");
  if (body.token_type !== "Bearer") {
    throw new Error("provider token type must be Bearer");
  }
  if (
    typeof body.expires_in !== "number" ||
    !Number.isSafeInteger(body.expires_in) ||
    body.expires_in <= 0
  ) {
    throw new Error("provider token expiry must be a positive integer");
  }
  return {
    accessToken,
    refreshToken,
    expiresAt: now + body.expires_in * 1_000,
  };
}

function canonicalUuid(value: unknown, name: string): string {
  const normalized = exactString(value, name);
  if (!uuidPattern.test(normalized)) {
    throw new Error(`provider ${name} must be a canonical UUID`);
  }
  return normalized;
}

function exactNullableUuid(value: unknown, name: string): string | null {
  if (value === null) return null;
  return canonicalUuid(value, name);
}

function exactPermissions(value: unknown): string[] {
  if (!Array.isArray(value)) throw new Error("provider permissions must be an array");
  const permissions = value.map((permission) => exactString(permission, "permission"));
  if (new Set(permissions).size !== permissions.length) {
    throw new Error("provider permissions must not contain duplicates");
  }
  return permissions;
}

function exactProfile(value: unknown): ProviderProfile {
  if (!value || typeof value !== "object") {
    throw new Error("provider profile must be an object");
  }
  const profile = value as Record<string, unknown>;
  return {
    email: exactString(profile.email, "profile email"),
    displayName: typeof profile.display_name === "string" ? profile.display_name.trim() : "",
    firstName: typeof profile.first_name === "string" ? profile.first_name.trim() : "",
    lastName: typeof profile.last_name === "string" ? profile.last_name.trim() : "",
  };
}

export function validateProviderSession(
  raw: unknown,
  config: ProviderAuthConfig,
): ProviderCoreSessionProjection {
  if (!raw || typeof raw !== "object") {
    throw new Error("provider session must be an object");
  }
  const body = raw as Record<string, unknown>;
  const accountUuid = canonicalUuid(body.account_uuid, "account UUID");
  const subject = canonicalUuid(body.subject, "subject");
  if (subject !== accountUuid) {
    throw new Error("provider subject must equal account UUID");
  }
  const accountType = exactString(body.account_type, "account type") as ProviderAccountType;
  if (accountType !== "wenlix_internal_manager") {
    throw new Error("provider account type is not allowed on the internal surface");
  }
  const organizationId = exactNumericId(
    typeof body.organization_id === "string" ? body.organization_id : undefined,
    "organization ID",
  );
  const studioId = exactNullableUuid(body.studio_id, "studio ID");
  const clientId = exactNumericId(
    typeof body.client_id === "string" ? body.client_id : undefined,
    "client ID",
  );
  if (clientId !== config.clientId) {
    throw new Error("provider client mismatch");
  }
  const consumerSurface = exactString(body.consumer_surface, "consumer surface");
  if (consumerSurface !== "internal_app") {
    throw new Error("provider consumer surface mismatch");
  }
  const legacySource = exactString(body.legacy_source, "legacy source");
  if (legacySource !== "app") {
    throw new Error("provider internal legacy source mismatch");
  }
  const legacyId = canonicalUuid(body.legacy_id, "legacy ID");
  if (body.legacy_projection !== true) {
    throw new Error("provider internal legacy projection is required");
  }
  const businessRole = exactString(body.role, "role");
  const businessApi = exactString(body.business_api, "business API");
  const permissions = exactPermissions(body.permissions);

  if (organizationId !== config.internalOrganizationId) {
    throw new Error("provider internal organization mismatch");
  }
  if (studioId !== null) throw new Error("provider internal manager studio must be null");
  if (!(["owner", "manager", "supervisor", "director"] as string[]).includes(businessRole)) {
    throw new Error("provider internal manager role mismatch");
  }
  if (businessApi !== "core") {
    throw new Error("provider internal manager business API mismatch");
  }
  const uiRole = businessRole as ProviderUiRole;

  return {
    accountUuid,
    accountType,
    subject,
    organizationId,
    clientId,
    consumerSurface,
    studioId,
    legacySource,
    legacyId,
    legacyProjection: true,
    businessRole,
    permissions,
    businessApi: businessApi as "core" | "staff",
    profile: exactProfile(body.profile),
    uiRole,
  };
}

function emptyRegistry(): StoredProviderRegistry {
  return { version: 1, activeAccountUuid: null, slots: {} };
}

function validTokens(tokens: unknown): tokens is ProviderTokens {
  if (!tokens || typeof tokens !== "object") return false;
  const value = tokens as ProviderTokens;
  return Boolean(
    typeof value.accessToken === "string" && value.accessToken &&
      typeof value.refreshToken === "string" && value.refreshToken &&
      typeof value.expiresAt === "number" && Number.isFinite(value.expiresAt),
  );
}

export class ProviderSlotRegistry {
  private readonly storage: StorageLike;

  constructor(storage: StorageLike) {
    this.storage = storage;
  }

  private read(): StoredProviderRegistry {
    const stored = this.storage.getItem(providerRegistryKey);
    if (!stored) return emptyRegistry();
    try {
      const parsed = JSON.parse(stored) as StoredProviderRegistry;
      if (parsed.version !== 1 || !parsed.slots || typeof parsed.slots !== "object") {
        throw new Error("unsupported registry");
      }
      for (const [accountUuid, slot] of Object.entries(parsed.slots)) {
        if (
          !uuidPattern.test(accountUuid) ||
          slot.projection?.accountUuid !== accountUuid ||
          slot.projection.subject !== accountUuid ||
          !validTokens(slot.tokens)
        ) {
          throw new Error("invalid slot");
        }
      }
      if (
        parsed.activeAccountUuid !== null &&
        !Object.hasOwn(parsed.slots, parsed.activeAccountUuid)
      ) {
        parsed.activeAccountUuid = null;
      }
      return parsed;
    } catch {
      this.storage.removeItem(providerRegistryKey);
      return emptyRegistry();
    }
  }

  private write(registry: StoredProviderRegistry): void {
    this.storage.setItem(providerRegistryKey, JSON.stringify(registry));
  }

  put(projection: ProviderSessionProjection, tokens: ProviderTokens): void {
    if (!uuidPattern.test(projection.accountUuid) || projection.subject !== projection.accountUuid) {
      throw new Error("provider slot requires a canonical UUID identity");
    }
    if (!validTokens(tokens)) throw new Error("provider slot requires exact tokens");
    const registry = this.read();
    registry.slots[projection.accountUuid] = {
      projection,
      tokens,
      updatedAt: Date.now(),
    };
    registry.activeAccountUuid = projection.accountUuid;
    this.write(registry);
  }

  activate(accountUuid: string): void {
    if (!uuidPattern.test(accountUuid)) {
      throw new Error("provider slot account must be a canonical UUID");
    }
    const registry = this.read();
    if (!Object.hasOwn(registry.slots, accountUuid)) {
      throw new Error("provider slot does not exist");
    }
    registry.activeAccountUuid = accountUuid;
    registry.slots[accountUuid].updatedAt = Date.now();
    this.write(registry);
  }

  deactivate(): void {
    const registry = this.read();
    registry.activeAccountUuid = null;
    this.write(registry);
  }

  active(): StoredProviderSlot | null {
    const registry = this.read();
    if (!registry.activeAccountUuid) return null;
    return registry.slots[registry.activeAccountUuid] ?? null;
  }

  rotateActiveTokens(
    accountUuid: string,
    expectedRefreshToken: string,
    tokens: ProviderTokens,
  ): void {
    if (!validTokens(tokens)) throw new Error("provider slot requires exact tokens");
    const registry = this.read();
    if (registry.activeAccountUuid !== accountUuid) {
      throw new Error("provider active slot changed during refresh");
    }
    const slot = registry.slots[accountUuid];
    if (!slot) throw new Error("provider active slot is missing");
    if (slot.tokens.refreshToken !== expectedRefreshToken) {
      throw new Error("provider refresh token changed during refresh");
    }
    slot.tokens = tokens;
    slot.updatedAt = Date.now();
    this.write(registry);
  }

  updateActiveProjection(projection: ProviderSessionProjection): void {
    const registry = this.read();
    if (!registry.activeAccountUuid) throw new Error("provider slot is not active");
    const slot = registry.slots[registry.activeAccountUuid];
    if (!slot) throw new Error("provider active slot is missing");
    const previous = slot.projection;
    if (
      projection.accountUuid !== previous.accountUuid ||
      projection.subject !== previous.subject ||
      projection.accountType !== previous.accountType ||
      projection.organizationId !== previous.organizationId ||
      projection.studioId !== previous.studioId ||
      projection.businessApi !== previous.businessApi
    ) {
      throw new Error("provider session account substitution was refused");
    }
    slot.projection = projection;
    slot.updatedAt = Date.now();
    this.write(registry);
  }

  updateActiveSurfaceProjection(projection: ProviderSessionProjection): void {
    const registry = this.read();
    const slot = registry.activeAccountUuid ? registry.slots[registry.activeAccountUuid] : undefined;
    const previous = slot?.projection;
    if (!slot || !previous || projection.accountUuid !== previous.accountUuid || projection.subject !== previous.subject ||
        projection.accountType !== previous.accountType || projection.organizationId !== previous.organizationId ||
        projection.clientId !== previous.clientId || projection.studioId !== previous.studioId ||
        (projection.businessApi !== previous.businessApi && !(projection.supportOnly || previous.supportOnly))) {
      throw new Error("provider session surface substitution was refused");
    }
    slot.projection = projection;
    slot.updatedAt = Date.now();
    this.write(registry);
  }

  remove(accountUuid: string): void {
    if (!uuidPattern.test(accountUuid)) {
      throw new Error("provider slot account must be a canonical UUID");
    }
    const registry = this.read();
    delete registry.slots[accountUuid];
    if (registry.activeAccountUuid === accountUuid) registry.activeAccountUuid = null;
    this.write(registry);
  }

  removeIfRefreshToken(accountUuid: string, expectedRefreshToken: string): boolean {
    if (!uuidPattern.test(accountUuid) || !expectedRefreshToken) return false;
    const registry = this.read();
    const slot = registry.slots[accountUuid];
    if (!slot || slot.tokens.refreshToken !== expectedRefreshToken) return false;
    delete registry.slots[accountUuid];
    if (registry.activeAccountUuid === accountUuid) registry.activeAccountUuid = null;
    this.write(registry);
    return true;
  }

  list(): ProviderSlotSummary[] {
    const registry = this.read();
    return Object.values(registry.slots)
      .sort((left, right) => right.updatedAt - left.updatedAt)
      .map(({ projection, tokens }) => ({
        accountUuid: projection.accountUuid,
        displayName: projection.profile.displayName,
        email: projection.profile.email,
        expiresAt: tokens.expiresAt,
      }));
  }

  projection(accountUuid: string): ProviderSessionProjection | null {
    if (!uuidPattern.test(accountUuid)) return null;
    return this.read().slots[accountUuid]?.projection ?? null;
  }
}
